/**
 * Normalizes socket and poll events into one alert stream.
 *
 * Dependencies are injected so this class stays usable in browser code and
 * focused node tests without framework or package imports.
 */
export class InboxAlertDispatcher {
  constructor({
    onCustomerMessage = () => {},
    onHandoff = () => {},
    acceptEvent = () => true,
    fingerprint = defaultFingerprint,
  } = {}) {
    this.onCustomerMessage = onCustomerMessage;
    this.onHandoff = onHandoff;
    this.acceptEvent = acceptEvent;
    this.fingerprint = fingerprint;
  }

  dispatchSocketMessage(data) {
    const event = this.normalizeMessage(data, "socket");
    if (!event || !this.acceptEvent(event.dedupeKey, event.dedupeOptions)) return false;
    this.onCustomerMessage(event);
    return true;
  }

  dispatchSocketHandoff(data) {
    const event = this.normalizeHandoff(data, "socket");
    if (!event || !this.acceptEvent(event.dedupeKey, event.dedupeOptions)) return false;
    this.onHandoff(event);
    return true;
  }

  dispatchPoll({ messages = [], handoffs = [] } = {}) {
    const normalizedHandoffs = handoffs
      .map((handoff) => this.normalizeHandoff(handoff, "poll"))
      .filter(Boolean);
    const normalizedMessages = messages
      .map((message) => this.normalizeMessage(message, "poll"))
      .filter(Boolean);

    for (const handoff of normalizedHandoffs) {
      if (this.acceptEvent(handoff.dedupeKey, handoff.dedupeOptions)) {
        this.onHandoff(handoff);
      }
    }

    for (const message of normalizedMessages) {
      const matchingHandoffs = normalizedHandoffs.filter(
        (handoff) => handoff.sessionId === message.sessionId,
      );
      const consumedByHandoff = matchingHandoffs.some(
        (handoff) =>
          handoff.createdAt == null ||
          message.createdAt == null ||
          message.createdAt <= handoff.createdAt,
      );

      // Mark the triggering/pre-handoff message seen so a later socket copy
      // cannot produce a second alert. The original poll arrays remain intact
      // for conversation-list update consumers.
      if (consumedByHandoff) {
        this.acceptEvent(message.dedupeKey, message.dedupeOptions);
        continue;
      }
      if (this.acceptEvent(message.dedupeKey, message.dedupeOptions)) {
        this.onCustomerMessage(message);
      }
    }

    return {
      handoffs: normalizedHandoffs.length,
      messages: normalizedMessages.length,
    };
  }

  normalizeMessage(data, source) {
    if (!data || !isCustomerEvent(data)) return null;
    const sessionId = clean(data.session_id ?? data.sessionId);
    if (!sessionId) return null;
    const message = clean(data.message ?? data.message_content);
    const createdAt = normalizeTimestamp(data.created_at ?? data.createdAt);
    const messageId = canonicalMessageId(data.message_id ?? data.messageId);
    const fallback = !messageId;
    return {
      type: "customer_message",
      source,
      sessionId,
      messageId,
      message,
      createdAt,
      dedupeKey: messageId
        ? `message:${messageId}`
        : `message:${this.fingerprint([sessionId, createdAt ?? "", message])}`,
      dedupeOptions: { fallback },
    };
  }

  normalizeHandoff(data, source = "poll") {
    if (!data) return null;
    const sessionId = clean(data.session_id ?? data.sessionId);
    if (!sessionId) return null;
    const handoffId = canonicalHandoffId(data.handoff_id ?? data.handoffId);
    const reasonType = clean(data.reason_type ?? data.reasonType);
    const createdAt = normalizeTimestamp(data.created_at ?? data.createdAt);
    const fallback = !handoffId;
    return {
      type: "handoff",
      source,
      sessionId,
      handoffId,
      reasonType,
      createdAt,
      dedupeKey: handoffId
        ? `handoff:${handoffId}`
        : `handoff:${this.fingerprint([sessionId, createdAt ?? "", reasonType])}`,
      dedupeOptions: { fallback },
    };
  }
}

export function canonicalMessageId(value) {
  const id = clean(value);
  if (!id) return null;
  if (/^\d+$/.test(id)) return `cm-${id}`;
  if (/^cm-\d+$/i.test(id)) return id.toLowerCase();
  return id;
}

export function canonicalHandoffId(value) {
  const id = clean(value);
  if (!id) return null;
  if (/^\d+$/.test(id)) return `hf-${id}`;
  if (/^hf-\d+$/i.test(id)) return id.toLowerCase();
  return id;
}

function isCustomerEvent(data) {
  const sender = clean(data.sender_type ?? data.sender ?? data.author_type).toLowerCase();
  const messageType = clean(data.message_type ?? data.type).toLowerCase();
  if (sender && !["customer", "user", "visitor"].includes(sender)) return false;
  if (["note", "internal_note", "system", "agent", "agent_reply"].includes(messageType)) return false;
  if (data.is_internal === true) return false;
  return true;
}

function normalizeTimestamp(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric < 10_000_000_000 ? numeric * 1000 : numeric;
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

function clean(value) {
  return value == null ? "" : String(value).trim();
}

function defaultFingerprint(parts) {
  const source = parts.map((part) => clean(part)).join("\u001f");
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fp-${(hash >>> 0).toString(36)}`;
}
